// features/workstations/qqq/today/composite-card.js
// ═══════════════════════════════════════════════════════════
// Pre-Event Composite Card — the headline new feature
// ═══════════════════════════════════════════════════════════
//
// Per docs/QQQ_DASHBOARD_REBUILD_SPEC.md Section II + IV-A and
// docs/QQQ_EVENT_DAY_PLAYBOOK.md Section II/III.
//
// Consumes /api/qqq/composite/today (Phase 2a-2). Renders:
//   - Composite score + conviction band
//   - 6-signal breakdown table with per-signal scores + reasons
//   - Decision call-out (TAKE / SKIP / WAIT)
//   - Drill-down: click any signal to expand its raw data
//   - Refresh button (calls ?fresh=true)
//
// Renders ALWAYS (composite scores even on non-event days for
// observation log) but visually emphasizes EVENT DAY status when
// relevant.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';

const BAND_DISPLAY = {
  high_dovish:       { label: 'HIGH-CONVICTION DOVISH',     tone: 'sage',   range: '+5 to +12' },
  moderate_dovish:   { label: 'MODERATE DOVISH',             tone: 'sage',   range: '+2 to +4'  },
  mixed:             { label: 'MIXED — NO EDGE',             tone: 'gold',   range: '-1 to +1'  },
  moderate_hawkish:  { label: 'MODERATE HAWKISH',            tone: 'terra',  range: '-4 to -2'  },
  high_hawkish:      { label: 'HIGH-CONVICTION HAWKISH',     tone: 'terra',  range: '-12 to -5' },
};

export default function CompositeCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async (fresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const url = `/api/qqq/composite/today${fresh ? '?fresh=true' : ''}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (res.status === 404) {
        const j = await res.json();
        setData({ status: 'no_snapshot', hint: j.hint });
        return;
      }
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
      }
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  if (loading && !data) {
    return <CardShell><div className="qqq-composite-card__loading">Loading composite…</div></CardShell>;
  }
  if (error) {
    return <CardShell><div className="qqq-composite-card__error">Error: {error}</div></CardShell>;
  }
  if (data?.status === 'no_snapshot') {
    return (
      <CardShell>
        <div className="qqq-composite-card__no-snapshot">
          <div>No composite snapshot for today yet.</div>
          <div className="qqq-composite-card__hint">{data.hint}</div>
          <button type="button" onClick={() => load(true)} className="qqq-composite-card__refresh-btn">
            Compute live now
          </button>
        </div>
      </CardShell>
    );
  }

  const band = BAND_DISPLAY[data.conviction_band] || { label: data.conviction_band, tone: 'gold', range: '' };
  const eventDayBadge = data.is_event_day
    ? <span className={`qqq-composite-card__event-badge qqq-composite-card__event-badge--${data.event_class || 'event'}`}>
        EVENT DAY: {(data.event_class || 'event').toUpperCase()}
      </span>
    : <span className="qqq-composite-card__event-badge qqq-composite-card__event-badge--normal">NORMAL DAY</span>;

  return (
    <CardShell>
      <div className="qqq-composite-card__header">
        <div className="qqq-composite-card__title">Pre-Event Composite</div>
        {eventDayBadge}
        <button type="button" onClick={() => load(true)} className="qqq-composite-card__refresh-btn">
          Refresh
        </button>
      </div>

      <div className={`qqq-composite-card__score qqq-composite-card__score--${band.tone}`}>
        <div className="qqq-composite-card__score-value">
          {data.composite_score >= 0 ? '+' : ''}{data.composite_score}
        </div>
        <div className="qqq-composite-card__score-band">{band.label}</div>
        <div className="qqq-composite-card__score-range">range {band.range}</div>
      </div>

      <div className={`qqq-composite-card__decision qqq-composite-card__decision--${data.decision_class}`}>
        {data.decision_text}
      </div>

      <div className="qqq-composite-card__breakdown">
        <div className="qqq-composite-card__breakdown-header">
          <span>Signal</span>
          <span>Score</span>
          <span>Reason</span>
        </div>
        {(data.breakdown || []).map((row, i) => {
          const isOpen = expanded[i];
          const scoreClass = row.score > 0 ? 'positive' : row.score < 0 ? 'negative' : 'neutral';
          return (
            <div key={i} className="qqq-composite-card__signal-row">
              <button
                type="button"
                className="qqq-composite-card__signal-trigger"
                onClick={() => setExpanded(p => ({ ...p, [i]: !p[i] }))}
              >
                <span className="qqq-composite-card__signal-name">
                  {isOpen ? '▾ ' : '▸ '}{row.name}
                </span>
                <span className={`qqq-composite-card__signal-score qqq-composite-card__signal-score--${scoreClass}`}>
                  {row.score > 0 ? '+' : ''}{row.score}
                </span>
                <span className="qqq-composite-card__signal-detail">{row.detail}</span>
              </button>
              {isOpen && data.signals && (
                <pre className="qqq-composite-card__signal-raw">
                  {JSON.stringify(rawForSignal(row.name, data.signals), null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>

      <div className="qqq-composite-card__footer">
        <span>Snapshot: {data.snapshot_at ? new Date(data.snapshot_at).toLocaleTimeString() : 'n/a'}</span>
        <span>Source: {data.source || 'cached'}</span>
      </div>
    </CardShell>
  );
}

function rawForSignal(name, signals) {
  // Map breakdown row name back to signals object key
  const map = {
    '10Y Treasury yield change':  signals.treasury_yield,
    'VIX term structure':         signals.vix_term,
    '25Δ options skew':           signals.options_skew,
    'Sector rotation':            signals.sector_rotation,
    'Stock-bond divergence':      signals.stock_bond_divergence,
    'Whisperer / personnel news': signals.whisperer,
  };
  return map[name] || { note: 'no raw data for signal' };
}

function CardShell({ children }) {
  return <div className="qqq-composite-card">{children}</div>;
}
